import {_utils} from "../../../utils/_utils.js";
import {_state} from "../../../state/state.js";

export const vacancies = () => {
    return new Promise((resolve) => {
        _utils.initSection('.vacancies', (section) => init(section));
        resolve();
    });
};

const init = (section) => {
    initAccordion(section);
    initPopup(section);
};

/* ---------- Accordion ---------- */
const initAccordion = (section) => {
    const items = [...section.querySelectorAll('.vacancies__item')];

    items.forEach((item) => {
        const head = item.querySelector('.vacancies__head');
        if (!head) return;

        head.addEventListener('click', () => {
            const willOpen = !item.classList.contains('is-open');

            // single-open accordion
            items.forEach((other) => {
                other.classList.remove('is-open');
                other.querySelector('.vacancies__head')?.setAttribute('aria-expanded', 'false');
            });

            if (willOpen) {
                item.classList.add('is-open');
                head.setAttribute('aria-expanded', 'true');
            }
        });
    });
};

/* ---------- Apply popup + CF7 ---------- */
const initPopup = (section) => {
    const popup = section.querySelector('[data-vac-popup]');
    if (!popup) return;

    const applyButtons = [...section.querySelectorAll('[data-vac-apply]')];
    const closeEls = [...popup.querySelectorAll('[data-vac-close]')];
    const formWrap = popup.querySelector('[data-vac-form]');
    const thanks = popup.querySelector('[data-vac-thanks]');
    const form = popup.querySelector('form.wpcf7-form') || popup.querySelector('form');
    const vacancyField = popup.querySelector('input[name="vacancy"]');

    let isOpen = false;

    // Scroll lock: stop the Lenis smooth-scroll instance (desktop) + the theme's
    // overflow-hidden class (handles native/touch scroll on mobile, where Lenis is off).
    const lockScroll = (lock) => {
        document.documentElement.classList.toggle('overflow-hidden', lock);
        document.body.classList.toggle('overflow-hidden', lock);
        if (_state.lenisInit) {
            lock ? _state.lenisInit.stop() : _state.lenisInit.start();
        }
    };

    const open = (vacancyName) => {
        if (vacancyField) vacancyField.value = vacancyName || '';
        // always start on the form view
        formWrap?.classList.remove('is-hidden');
        thanks?.classList.remove('is-visible');
        popup.classList.add('is-open');
        popup.setAttribute('aria-hidden', 'false');
        lockScroll(true);
        isOpen = true;
    };

    const close = () => {
        popup.classList.remove('is-open');
        popup.setAttribute('aria-hidden', 'true');
        lockScroll(false);
        isOpen = false;
    };

    applyButtons.forEach((btn) => {
        btn.addEventListener('click', () => open(btn.dataset.vacancy || ''));
    });
    closeEls.forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
        if (isOpen && e.key === 'Escape') close();
    });

    if (form) {
        initCvUpload(form);

        form.addEventListener('wpcf7mailsent', () => {
            formWrap?.classList.add('is-hidden');
            thanks?.classList.add('is-visible');
        });
    }
};

/* ---------- Custom CV upload field (wraps the CF7 [file] input) ---------- */
const ALLOWED = ['pdf', 'doc', 'docx'];
const isAllowed = (name) => ALLOWED.includes((name.split('.').pop() || '').toLowerCase());

const initCvUpload = (form) => {
    const box = form.querySelector('[data-cv]');
    if (!box) return;

    const input = box.querySelector('input[type="file"]');
    const titleEl = box.querySelector('[data-cv-title]');
    const actionEl = box.querySelector('[data-cv-action]');
    if (!input) return;

    const defaultTitle = titleEl ? titleEl.innerHTML : 'Upload your CV';

    const setState = (state, opts = {}) => {
        box.dataset.cvState = state;
        if (titleEl) {
            if (state === 'filled') titleEl.textContent = opts.name || '';
            else if (state === 'error') titleEl.textContent = opts.message || '';
            else if (state === 'uploading') titleEl.textContent = 'Uploading...';
            else titleEl.innerHTML = defaultTitle;
        }
        if (actionEl) {
            if (state === 'filled') actionEl.textContent = actionEl.dataset.delete || 'Delete file';
            else if (state === 'error') actionEl.textContent = actionEl.dataset.replace || 'Replace file';
            else actionEl.textContent = '';
        }
    };

    const openDialog = () => input.click();

    // Immediate sync (no "uploading" flash) — used to reflect CF7 events.
    const syncFromInput = () => {
        const file = input.files && input.files[0];
        if (!file) {
            setState('default');
        } else if (!isAllowed(file.name)) {
            setState('error', {message: box.dataset.errorMessage || 'Unsupported file type'});
        } else {
            setState('filled', {name: file.name});
        }
    };

    // On the user picking a file: show "Uploading…" while the browser reads it,
    // then switch to the filled state. (A native file input has nothing to upload
    // until submit, so the read is the real work we surface here.)
    const MIN_UPLOADING_MS = 500;
    let readToken = 0;
    input.addEventListener('change', () => {
        const file = input.files && input.files[0];
        if (!file) {
            setState('default');
            return;
        }
        if (!isAllowed(file.name)) {
            setState('error', {message: box.dataset.errorMessage || 'Unsupported file type'});
            return;
        }

        const token = ++readToken;
        setState('uploading');
        const startedAt = Date.now();
        const finish = (state, opts) => {
            if (token !== readToken) return; // superseded by a newer selection
            const wait = Math.max(0, MIN_UPLOADING_MS - (Date.now() - startedAt));
            setTimeout(() => {
                if (token === readToken) setState(state, opts);
            }, wait);
        };

        const reader = new FileReader();
        reader.onload = () => finish('filled', {name: file.name});
        reader.onerror = () => finish('error', {message: box.dataset.errorMessage || 'Could not read the file'});
        reader.readAsArrayBuffer(file);
    });

    // One delegated handler for the whole dropzone. Decide what was clicked by
    // walking up from the real target — this is immune to child pointer-events,
    // and the "came from the input" check prevents openDialog() from recursing.
    box.addEventListener('click', (e) => {
        if (actionEl && (e.target === actionEl || actionEl.contains(e.target))) {
            e.preventDefault();
            const state = box.dataset.cvState;
            if (state === 'filled') {
                input.value = '';
                setState('default');
            } else if (state === 'error') {
                openDialog();
            }
            return;
        }
        // ignore the input's own (programmatic) click; open on any other click
        if (e.target === input || e.target.closest('.wpcf7-form-control-wrap')) return;
        openDialog();
    });

    // reflect CF7 lifecycle states (no "uploading" here — that belongs to file read)
    form.addEventListener('wpcf7invalid', () => {
        const wrap = input.closest('.wpcf7-form-control-wrap');
        const tip = wrap && wrap.querySelector('.wpcf7-not-valid-tip');
        if (tip) {
            setState('error', {message: tip.textContent.trim()});
        } else {
            syncFromInput();
        }
    });
    form.addEventListener('wpcf7mailsent', () => {
        readToken++; // cancel any in-flight read
        input.value = '';
        setState('default');
    });
    ['wpcf7spam', 'wpcf7mailfailed'].forEach((evt) => form.addEventListener(evt, syncFromInput));

    setState('default');
};
